import type { TrialPlan, Condition } from './types';
import { hashStringToSeed, mulberry32, shuffle } from './rng';

const CONDITION_META: Record<Condition, { prompt: string; mechanic: string; hue: number; base: number }> = {
  calm: {
    prompt: 'Quiet scan. Let the field breathe. Do not count.',
    mechanic: 'A low-arousal baseline: soft periodic motion with no task demand.',
    hue: 190,
    base: 7800
  },
  signal: {
    prompt: 'Signal pressure. Watch for anomalies, then let them go.',
    mechanic: 'High-arousal threat-cue analogue: unstable motion, urgent signals, no harm or gore.',
    hue: 4,
    base: 8600
  },
  flow: {
    prompt: 'Flow thread. Tap when the comet crosses the gate.',
    mechanic: 'Attention capture / flow: a just-hard-enough timing game diverts attention from time.',
    hue: 265,
    base: 9800
  },
  oddball: {
    prompt: 'Oddball field. Repetition, repetition, violation.',
    mechanic: 'Temporal oddball: a novel event follows repeated standards and often feels longer.',
    hue: 48,
    base: 7200
  }
};

export function buildSchedule(deviceKey: string, runSeed = Date.now()): TrialPlan[] {
  const seed = hashStringToSeed(`${deviceKey}:${runSeed}`);
  const rand = mulberry32(seed);
  const conditions: Condition[] = ['calm', 'signal', 'flow', 'oddball'];
  const blocks = [0, 1].flatMap((rep) => shuffle(conditions, rand).map((condition) => ({ condition, rep })));

  return blocks.map(({ condition, rep }, index) => {
    const meta = CONDITION_META[condition];
    const jitter = Math.round((rand() - 0.5) * 1800);
    return {
      id: `${condition}-${rep + 1}-${index + 1}`,
      condition,
      trialIndex: index + 1,
      plannedMs: Math.max(5200, meta.base + jitter),
      prompt: meta.prompt,
      mechanic: meta.mechanic,
      hue: meta.hue
    } satisfies TrialPlan;
  });
}

export function conditionLabel(condition: Condition): string {
  return {
    calm: 'Calm Scan',
    signal: 'Signal Pressure',
    flow: 'Flow Thread',
    oddball: 'Oddball Field'
  }[condition];
}

export function conditionShort(condition: Condition): string {
  return {
    calm: 'baseline',
    signal: 'arousal',
    flow: 'flow',
    oddball: 'novelty'
  }[condition];
}
